/** @typedef */
import { useMemo } from "react";

/**
 * takes in an array of members and splits them into categories
 * @param {[HouseholdMember]} members
 * @returns {CategorizedHouseholdMembers}
 */
const useCategorizedMembers = (members = []) => {
  const hohs = [];
  const dependents = [];
  const children = [];
  const nonHohs = [];
  members.forEach((member) => {
    // non heads of household will be grouped together in nonHohs here, but also separated into children and dependants.
    if (!member.isHOH) nonHohs.push(member);
    if (member.isHOH) hohs.push(member);
    else if (member.isChild) children.push(member);
    else dependents.push(member);
  });
  return { hohs, dependents, children, nonHohs };
};

/**
 * takes in an array of members and splits them into categories. Category criteria for page is diverging from profile. Following standards and duplicating.
 * @param {[HouseholdMember]} members
 * @returns {CategorizedHouseholdMembers}
 */
const useCategorizedMembersForDatabasePage = (members = []) => {
  const hohs = [];
  const dependents = [];
  const children = [];
  const nonHohs = [];
  members.forEach((member) => {
    // non heads of household will be grouped together in nonHohs here, but also separated into children and dependants.
    if (!member.isHOH) {
      nonHohs.push(member);
    }
    if (member.isHOH) {
      hohs.push(member);
    } else if (member.isChild) {
      children.push(member);
    } else {
      member.spouseUserID ? hohs.push(member) : dependents.push(member);
    }
  });
  return { hohs, dependents, children, nonHohs };
};

/**
 * takes in an array of members and splits them into categories
 * @param {[HouseholdMember]} members
 * @returns {number}
 */
const useHohID = (members = []) => {
  const { hohs, dependents, children } = useCategorizedMembers(members);

  return useMemo(() => {
    let repID;
    repID = hohs.find(({ userID }) => Boolean(userID));
    if (repID) return repID.userID;
    repID = dependents.find(({ userID }) => Boolean(userID));
    if (repID) return repID.userID;
    repID = children.find(({ userID }) => Boolean(userID));
    if (repID) return repID.userID;
    return undefined;
  }, [children, dependents, hohs]);
};

/**
 * takes in an array of networks and splits them into categories
 * @param {[HouseholdNetwork]} networks
 * @param {number} currentlySelectedSubnetworkID
 * @returns {CategorizedHouseholdNetworks}
 */
const useCategorizedNetworks = (
  networks = [],
  currentlySelectedSubnetworkID
) => {
  let primary;
  let current;
  const secondary = [];
  networks.forEach((network) => {
    // current and secondary and not mutually exclusive, ignore attempted current overrides
    if (network.isCurrent && !currentlySelectedSubnetworkID) current = network;

    // if there is a currently selected subnetwork, use that as the current network (this is for the subnetworks dropdown and allows the parentnet to display the subnet status in badge)
    if (
      currentlySelectedSubnetworkID &&
      network.networkID === currentlySelectedSubnetworkID
    )
      current = network;

    // primary and seconary ARE mutually exclusive
    if (network.isPrimary) primary = network;
    else secondary.push(network);
  });

  return { primary, secondary, current };
};

export {
  useCategorizedMembers,
  useCategorizedMembersForDatabasePage,
  useCategorizedNetworks,
  useHohID,
};
